import DateTimepicker from "components/ui/DatePicker/DateTimepicker"
import capitalize from "components/ui/utils/capitalize"
import { RuleGroupsContext } from "contexts/RuleGroupsContext"
import { SessionContext } from "contexts/SessionContext"
import { useWhitelabel } from "contexts/WhitelabelThemeContext"
import { useContext, useEffect, useState } from "react"
import { BsStars } from "react-icons/bs"
import usePublishAtSuggestion from "utils/hooks/usePublishAtSuggestion"
import { formatInUserTz, utcToUserTzDate, userTzDateToUTC } from "utils/timezone"

const { Button, Dropdown, Switcher, Tooltip, Skeleton } = require("components/ui")
const { HiOutlineCheckCircle, HiOutlineExclamationCircle, HiXCircle } = require("react-icons/hi2")
const { TbShare2, TbShare3, TbClock, TbClockX, TbCalendarBolt } = require("react-icons/tb")

export const getExtraErrorMessages = ({ whitelabel, isWhitelabel } = {}) => [
    {
        network: 'youtube',
        whenIncludes: "have permissions to upload and set custom video thumbnails",
        add: <>
            {" "}Please verify your account by putting telephone number and enable "customised thumbnails" in your account's panel.
            {(!isWhitelabel || !!whitelabel?.helpLink) && (
                <a href={whitelabel?.helpLink || "https://help.followr.ai/en/articles/12302872-how-to-verify-your-youtube-account"} target="_blank" className="text-primary-500">
                    {" "}Docs
                </a>
            )}
        </>
    }
];

const PostArticleButton = ({
    postGroup,
    onSave,
    status,
    onPostNow = undefined,
    updateFieldValuePostGroup = undefined,
    showSaveButton = undefined,
    isLoading = false,
    size = "sm",
    placement = "bottom-end"
}) => {

    const { user } = useContext(SessionContext);
    const { getAutomatedPublishAt } = useContext(RuleGroupsContext);
    const { getAiSuggestedDateTime } = usePublishAtSuggestion();
    const { whitelabel, isWhitelabel } = useWhitelabel();
    const extraErrorMessages = getExtraErrorMessages({ whitelabel, isWhitelabel });

    const isAutolisted = !!getAutomatedPublishAt(postGroup);
    const automatedPublishAt = getAutomatedPublishAt(postGroup);

    const [ isDatePickerOpened, setIsDatePickerOpened ] = useState(false);
    const [ aiSuggestedDateTime, setAiSuggestedDateTime ] = useState(undefined);
    const [ aiSuggestedDateTimeExplanation, setAiSuggestedDateTimeExplanation ] = useState(undefined);
    const [ isLoadingAiSuggestedDateTime, setIsLoadingAiSuggestedDateTime ] = useState(false);
    const [ dateTime, setDateTime ] = useState(postGroup?.publish_at || automatedPublishAt
        ? utcToUserTzDate(postGroup?.publish_at || automatedPublishAt, user)
        : new Date()
    );
    const errors = postGroup?.posts?.filter(post => post?.status == "error")?.map(post => {
        return { network: post?.network || post?.social_network_type, error: post?.status_message }
    });

    const handleAiSuggestedDateTime = async () => {
        if (
            !isAutolisted && 
            !automatedPublishAt && 
            !postGroup?.publish_at && 
            !isLoadingAiSuggestedDateTime 
        ) {
            setIsLoadingAiSuggestedDateTime(true);
            try {
                const { date, explanation } = await getAiSuggestedDateTime(postGroup?.posts?.[0]?.text || postGroup?.posts?.[0]?.description);
                setAiSuggestedDateTime(date);
                setAiSuggestedDateTimeExplanation(explanation);
            } catch (err) { console.error(err) }
            setIsLoadingAiSuggestedDateTime(false);
        }
    }

    const resetState = () => {
        setIsDatePickerOpened(false);
    }

    const handleOnOpen = () => {
        resetState();
        handleAiSuggestedDateTime();
    }

    useEffect(() => {
        resetState();
    }, [postGroup?.publish_at]);

    return (<>
        {(!postGroup?.id || showSaveButton) 
            ? <Button 
                variant="twoTone"
                color="primary-600"
                size={size}
                className="!rounded-xl !shrink-0"
                icon={<HiOutlineCheckCircle />}
                onClick={onSave}
                loading={isLoading}
            >
                Save
            </Button>
            : <Dropdown
                placement={placement}
                parentClassName={`w-full`}
                menuClass={`w-[235px] ${isDatePickerOpened && `!w-auto !py-0`} ${!!errors?.length && `!w-[250px]`}`}
                disabled={status == "published" || status == "publishing"}
                onOpen={handleOnOpen}
                renderTitle={
                    <Button 
                        variant={status == "published" || status == "publishing" ? "filled" : "twoTone"}
                        color={
                            !!postGroup?.draft
                                ? "gray-800"
                                : status == "published" 
                                    ? "emerald-500"
                                    : status == "publishing" 
                                        ? "cyan-800"
                                        : status == "error" 
                                            ? "red-600"
                                            : status == "pending" && !isAutolisted && !!postGroup?.publish_at 
                                                ? "purple-600"
                                                : status == "pending" && !!isAutolisted 
                                                    ? "purple-600"
                                                    : "primary-600"
                        }
                        size={size}
                        disabled={status == "published" || status == "publishing"}
                        className="!rounded-xl"
                        loading={status == "publishing" || isLoading}
                        icon={
                            status == "published"
                                ? <HiOutlineCheckCircle />
                                : status == "error"
                                    ? <HiOutlineExclamationCircle />
                                    : status == "pending" && !isAutolisted && !!postGroup?.publish_at
                                        ? <TbClock />
                                        : status == "pending" && !!isAutolisted
                                            ? <TbCalendarBolt />
                                            : <TbShare2 />
                        }
                    >
                        {status == "published"
                            ? <>Published</>
                            : status == "publishing" 
                                ? <>Publishing</>
                                : status == "error"
                                    ? <>Error</> 
                                    : status == "pending" && !isAutolisted && !!postGroup?.publish_at
                                        ? <>{formatInUserTz(postGroup?.publish_at, user, "MMM D")}</>
                                        : status == "pending" && !!isAutolisted
                                            ? <>{formatInUserTz(automatedPublishAt, user, "MMM D")}</>
                                            : <>Publish</>
                        }
                        {postGroup?.draft && ` (draft)`}
                    </Button>
                }
            >
                {isDatePickerOpened
                    ? <div className="relative">
                        <DateTimepicker  
                            value={dateTime}
                            onChange={(val) => setDateTime(val)}
                            okButtonVariant="twoTone"
                            pickerClassName="!relative !transform-none"
                            defaultOpen={true}
                            placement="bottom-end"
                            fieldsetClassName={`hidden w-full !rounded bg-tertiary-50 dark:bg-gray-900 !shadow !border-0 ${(status == "published" || status == "publishing") && `!pr-0 !pointer-events-none !bg-secondary-500 dark:!bg-gray-800 !shadow-none !pl-0 !-ml-2`}`}
                            inputFormat="MMM D - h:mm a"
                            okButtonContent="Save"
                            minDate={new Date()}
                            onOk={(val) => updateFieldValuePostGroup('publish_at', val ? userTzDateToUTC(new Date(val), user) : val)}
                        />
                    </div>
                    : <>
                        {status == "pending" && !isAutolisted && !!postGroup?.publish_at &&
                            <Dropdown.Item variant="header" className="font-semibold flex flex-col items-center gap-2 py-2.5 px-4 bg-purple-600/5 text-purple-600 -mt-1.5 text-center">
                                Scheduled: {formatInUserTz(postGroup?.publish_at, user, "MMM D H:mm")}hs
                            </Dropdown.Item>
                        }
                        {status == "pending" && !!isAutolisted &&
                            <Dropdown.Item variant="header" className="font-semibold flex flex-col items-center gap-2 py-2.5 px-4 bg-purple-600/5 text-purple-600 -mt-1.5 text-center">
                                Auto-Listed: {formatInUserTz(automatedPublishAt, user, "MMM D H:mm")}hs
                            </Dropdown.Item>
                        }
                        {!!errors?.length && 
                            <Dropdown.Item variant="header" className="flex flex-col items-center gap-2 py-2 px-4 bg-red-600/5 text-red-600 -mt-1.5">
                                {errors.map((error) => (
                                    <div className="flex items-center justify-center gap-4">
                                        <HiXCircle className="text-xl shrink-0" />
                                        <span>
                                            <b>{capitalize(error.network)}:</b> <span>{error.error}</span>
                                            {extraErrorMessages.find(e => error.error?.includes(e.whenIncludes) && e.network == error.network) && extraErrorMessages.find(e => error.error?.includes(e.whenIncludes) && e.network == error.network).add}
                                        </span>
                                    </div>
                                ))}
                            </Dropdown.Item>
                        }
                        {postGroup?.draft && 
                            <Dropdown.Item variant="header" className="font-semibold flex flex-col items-center gap-2 py-2.5 px-4 bg-gray-600/5 mb-1.5 text-tertiary-600 text-center">
                                <span className="font-normal text-center">This post is marked as <b>draft</b>. Draft posts are not published automatically.</span>
                            </Dropdown.Item>
                        }
                        {status != "published" && <>
                            <Dropdown.Item className="flex items-center gap-2 py-2 px-4">
                                <div onClick={(e) => { e.stopPropagation(); updateFieldValuePostGroup('draft', !!postGroup?.draft ? 0 : 1)}} className="w-full h-full flex items-center gap-2">
                                    <Switcher
                                        onChange={(value) => {}}
                                        value={true}
                                        checked={!!postGroup?.draft}
                                        className="pointer-events-auto"
                                        disabled={isLoading}
                                    />
                                    {!postGroup?.draft 
                                        ? <span>Mark as draft</span>
                                        : <span>Unmark as draft</span>
                                    }
                                </div>
                            </Dropdown.Item>
                            <Dropdown.Item variant="divider" />
                        </>}
                        {!postGroup?.draft && 
                            <Dropdown.Item onClick={onPostNow} className="flex items-center gap-2 py-2 px-4">
                                <TbShare3 className='h-6 md:text-2xl text-primary-600' />
                                <span>Post now</span>
                            </Dropdown.Item>
                        }
                        <Dropdown.Item className="!p-0">
                            <div onClick={(e) => { setIsDatePickerOpened(true); e.stopPropagation(); }} className="w-full h-ful flex items-center gap-2 cursor-pointer py-2 px-4">
                                <TbClock className='h-6 md:text-2xl text-purple-500' />
                                {postGroup?.publish_at 
                                    ? <span>Change date and time...</span>
                                    : <span>Schedule for later...</span>
                                }
                            </div>
                        </Dropdown.Item>
                        {((!!aiSuggestedDateTime && !postGroup?.publish_at) || isLoadingAiSuggestedDateTime) && 
                            <Dropdown.Item 
                                onClick={() => updateFieldValuePostGroup(`publish_at`, aiSuggestedDateTime)} 
                                className="p-2"
                            >
                                {!isLoadingAiSuggestedDateTime
                                    ? <Tooltip placement="bottom" wrapperClass="w-full" title={`AI Scheduling Suggestion: ${aiSuggestedDateTimeExplanation}`} disabled={!aiSuggestedDateTimeExplanation}>
                                        <div className="flex items-center gap-2 py-2 px-4 bg-gradient-ai transition-all hover:brightness-[1.15] text-white rounded-xl w-full">
                                            <BsStars className='h-6 md:text-2xl text-white' />
                                            <div className="flex flex-col">
                                                <span className="font-normal"><b>AI</b> - Schedule post at:</span>
                                                <span className="font-semibold">{formatInUserTz(aiSuggestedDateTime, user, "MMM DD, YYYY hh:mm A")}</span>
                                            </div>
                                        </div>
                                    </Tooltip>
                                    : <Skeleton 
                                        className="w-full h-[52px] rounded-xl"
                                    />
                                }
                            </Dropdown.Item>
                        }
                        {!!postGroup?.publish_at && <Dropdown.Item onClick={() => updateFieldValuePostGroup('publish_at', null)} className="flex items-center gap-2 py-2 px-4">
                            <TbClockX className='h-6 md:text-2xl text-tertiary-800 dark:text-gray-200' />
                            <span>Unschedule</span>
                        </Dropdown.Item>}
                    </>
                }
            </Dropdown>
        }
    </>)
}

export default PostArticleButton;